// Helpers over GitHub's unified patches (the `patch` field of pulls/:n/files).
import type { PatchHunk } from '../engine/types';

const U32_MAX = 0xffff_ffff;

/**
 * Parses a unified patch into hunks, as the engine's `parse_patch`
 * (crates/engine/src/patch.rs) does: an optional preamble (ignored),
 * `@@ -a[,b] +c[,d] @@ header` hunk headers (omitted counts mean 1), and
 * ` `/`+`/`-` rows. A `\ No newline at end of file` marker sets `noNewline`
 * on the row directly before it and is ignored after anything else. An
 * empty line inside a hunk is a blank context line. A row past its side's
 * header count, lines after a hunk's counts are used up, a header whose
 * range would end past u32::MAX, and anything else malformed are ignored;
 * this never throws.
 */
export function parsePatch(patch: string): PatchHunk[] {
  const hunks: PatchHunk[] = [];
  // Remaining old/new lines of the current hunk and the next line numbers.
  let remOld = 0;
  let remNew = 0;
  let o = 0;
  let n = 0;
  // Whether the previous line was a row this parser kept.
  let afterRow = false;
  for (const line of patch.split('\n')) {
    const wasRow = afterRow;
    afterRow = false;
    const header = line.startsWith('@@') ? parseHeader(line) : null;
    if (header) {
      hunks.push(header);
      [remOld, remNew] = [header.oldLines, header.newLines];
      [o, n] = [Math.max(header.oldStart, 1), Math.max(header.newStart, 1)];
      continue;
    }
    const hunk = hunks.at(-1);
    if (!hunk) continue;
    if (line.startsWith('\\')) {
      if (wasRow) hunk.rows[hunk.rows.length - 1].noNewline = true;
      continue;
    }
    if (remOld === 0 && remNew === 0) continue;
    const sign = line === '' ? ' ' : line[0];
    const kind = sign === '+' ? 'add' : sign === '-' ? 'del' : sign === ' ' ? 'context' : null;
    if (!kind) continue;
    const hasOld = kind !== 'add';
    const hasNew = kind !== 'del';
    // A row beyond its side's count is malformed.
    if ((hasOld && remOld === 0) || (hasNew && remNew === 0)) continue;
    hunk.rows.push({ kind, old: hasOld ? o++ : null, new: hasNew ? n++ : null, text: line.slice(1), noNewline: false });
    if (hasOld) remOld--;
    if (hasNew) remNew--;
    afterRow = true;
  }
  return hunks;
}

/** `@@ -a[,b] +c[,d] @@ header`, or null when malformed. */
function parseHeader(line: string): PatchHunk | null {
  const rest = line.slice(2).trimStart();
  const close = rest.indexOf('@@');
  if (close < 0) return null;
  const [oldRange, newRange] = rest.slice(0, close).split(/\s+/).filter(Boolean);
  const old = oldRange?.startsWith('-') ? parseRange(oldRange.slice(1)) : null;
  const neu = newRange?.startsWith('+') ? parseRange(newRange.slice(1)) : null;
  if (!old || !neu) return null;
  return {
    oldStart: old[0],
    oldLines: old[1],
    newStart: neu[0],
    newLines: neu[1],
    header: rest.slice(close + 2).trim(),
    rows: [],
  };
}

/** `a[,b]` as [start, count], or null when the rows, numbered from
 *  max(start, 1), would pass u32::MAX. */
function parseRange(s: string): [number, number] | null {
  const m = /^(\d+)(?:,(\d+))?$/.exec(s);
  if (!m) return null;
  const start = Number(m[1]);
  const count = m[2] === undefined ? 1 : Number(m[2]);
  return Math.max(start, 1) + count <= U32_MAX ? [start, count] : null;
}

function splitLines(text: string): { lines: string[]; trailingNewline: boolean } {
  if (text === '') return { lines: [], trailingNewline: false };
  const trailingNewline = text.endsWith('\n');
  const lines = (trailingNewline ? text.slice(0, -1) : text).split('\n');
  return { lines, trailingNewline };
}

/**
 * Rebuild one side of a file from the other side plus the patch:
 * `direction: 'reverse'` gives old from new, `'forward'` gives new from old.
 */
export function applyPatch(text: string, patch: string, direction: 'forward' | 'reverse'): string {
  const { lines, trailingNewline } = splitLines(text);
  const hunks = parsePatch(patch);
  const out: string[] = [];
  const fromKey = direction === 'reverse' ? 'new' : 'old';
  const keep = direction === 'reverse' ? 'del' : 'add';
  const drop = direction === 'reverse' ? 'add' : 'del';
  let i = 0; // 0-based index into `lines`
  for (const h of hunks) {
    const start = (direction === 'reverse' ? h.newStart : h.oldStart) - 1;
    const hunkStart = (direction === 'reverse' ? h.newLines : h.oldLines) === 0 ? start + 1 : start;
    while (i < hunkStart && i < lines.length) out.push(lines[i++]);
    for (const r of h.rows) {
      if (r.kind === 'context') {
        out.push(r[fromKey] !== null && lines[i] !== undefined ? lines[i] : r.text);
        i++;
      } else if (r.kind === keep) out.push(r.text);
      else if (r.kind === drop) i++;
    }
  }
  while (i < lines.length) out.push(lines[i++]);
  if (!out.length) return '';
  return out.join('\n') + (trailingNewline || lines.length === 0 ? '\n' : '');
}
